import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import axios from '../../api/axios';
import { rebuildPollRunLeaderboardRoute } from '../../api/routes';
import './PollLoadingProgress.scss';

export default function PollLoadingProgress({ request, onStatusChange }) {
    const [job, setJob] = useState(null);
    useEffect(() => {
        if (!request?.projections?.length) {
            setJob(null);
            return;
        }
        let cancelled = false;
        let timer;
        setJob({ status: 'building', phase: 'starting', processed: 0, total: 0 });
        onStatusChange?.('building');
        const fail = () => {
            if (!cancelled) {
                setJob(null);
                onStatusChange?.('failed');
                toast.error('Unable to load poll scores. Reconnect to retry; startup can continue.');
            }
        };
        const update = (next) => {
            if (cancelled) return;
            setJob(next);
            if (next.status === 'building') {
                onStatusChange?.('building');
                timer = setTimeout(async () => {
                    try {
                        const response = await axios.get(`${rebuildPollRunLeaderboardRoute}/${next.id}`, { timeout: 15000 });
                        update(response.data.job);
                    } catch (error) { fail(); }
                }, 250);
            } else if (next.status === 'failed') fail();
            else if (next.status === 'ready') {
                // Small local datasets can finish between status requests.
                // Briefly show the real completed count instead of disappearing
                // straight from the initial indeterminate state.
                timer = setTimeout(() => {
                    setJob(null);
                    onStatusChange?.('ready');
                }, 1500);
            }
        };
        axios.post(rebuildPollRunLeaderboardRoute, request, { withCredentials: true, timeout: 15000 })
            .then((response) => update(response.data.job)).catch(fail);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [request]);

    if (!request?.projections?.length || !job || !['building', 'ready'].includes(job.status)) return null;
    const ready = job.status === 'ready';
    const percent = ready ? 100 : job.total ? Math.min(99, Math.round(job.processed / job.total * 100)) : undefined;
    return createPortal(<aside className="poll-loading-progress" aria-label="Poll loading status">
        <div role="status" aria-live="polite">
            <strong>{ready ? 'Polls Loaded' : 'Polls Loading'}</strong>
            <span>{ready ? `${job.total} of ${job.total} runs loaded` : job.phase === 'loading' || job.total > 0 ? `Loading ${job.processed} of ${job.total} runs` : job.phase === 'counting' ? 'Counting saved runs…' : 'Starting loader…'}</span>
        </div>
        <progress aria-label="Poll scores loaded" max="100" value={percent} />
    </aside>, document.body);
}
